import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { CaretLeft, CaretRight } from '@phosphor-icons/react';
import {
  API,
  authConfig,
  authHeaders,
  alertError,
  btnSecondary,
  thStyle,
  tdStyle,
  pageTitleStyle,
  pageSubtitleStyle,
  ticketCategoryLabel,
  ticketClientBranchLabel,
  parseTicketListResponse,
  TICKET_LIST_PAGE_SIZE,
} from '../api/ticketsApi';
import { formatUtcToIst } from '../utils/datetime';
import TicketStatusBadge from '../components/TicketStatusBadge';
import ListSearch from '../../../components/ListSearch';
import { TicketDetailLink } from '../ticketNav';
import { useProductFilter } from '../ProductFilterContext';

/** Normalize API timing values into the UI state machine. */
function normalizeTiming(status) {
  const s = (status || 'not_started').toLowerCase();
  if (s === 'in_progress' || s === 'restarted') return 'in_progress';
  if (s === 'on_hold') return 'on_hold';
  return 'not_started';
}

const TIMING_LABELS = {
  not_started: 'Not started',
  in_progress: 'In progress',
  on_hold: 'On hold',
};

/**
 * Only show the action that applies to the current timing state:
 *   not_started → Start
 *   in_progress → Hold (pause — other work came up)
 *   on_hold     → Resume (continue later)
 */
function actionsForTiming(status) {
  const state = normalizeTiming(status);
  if (state === 'not_started') {
    return [{ key: 'in_progress', label: 'Start', color: '#10B981' }];
  }
  if (state === 'in_progress') {
    return [{ key: 'on_hold', label: 'Hold', color: '#F59E0B' }];
  }
  if (state === 'on_hold') {
    return [{ key: 'restarted', label: 'Resume', color: '#3B82F6' }];
  }
  return [];
}

function TicketMyTasksPage() {
  const { productId, selectedProduct } = useProductFilter();
  const [tasks, setTasks] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updating, setUpdating] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const loadTasks = useCallback(async (pageNum, query, { silent = false } = {}) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const q = (query || '').trim();
      const res = await axios.get(`${API}/assignments/my-tasks`, authConfig({
        ...(productId ? { product_id: productId } : {}),
        ...(q ? { search: q } : {}),
        limit: TICKET_LIST_PAGE_SIZE,
        skip: (pageNum - 1) * TICKET_LIST_PAGE_SIZE,
      }));
      const { items, total: count } = parseTicketListResponse(res);
      setTasks(items);
      setTotal(count);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load tasks');
      setTasks([]);
      setTotal(0);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    setPage(1);
  }, [productId, searchQuery]);

  useEffect(() => {
    const t = setTimeout(() => loadTasks(page, searchQuery), 300);
    return () => clearTimeout(t);
  }, [loadTasks, page, searchQuery]);

  const handleTiming = async (ticketNo, timingStatus) => {
    setUpdating(ticketNo);
    setError(null);
    try {
      const res = await axios.patch(
        `${API}/assignments/${ticketNo}/timing`,
        { timing_status: timingStatus },
        authHeaders(),
      );
      const nextStatus = res.data?.timing_status
        || (timingStatus === 'restarted' ? 'in_progress' : timingStatus);
      // Optimistic UI — swap buttons immediately from the 200 response
      setTasks((prev) => prev.map((t) => (
        t.ticket_no === ticketNo
          ? { ...t, timing_status: nextStatus }
          : t
      )));
      await loadTasks(page, searchQuery, { silent: true });
    } catch (err) {
      setError(err.response?.data?.detail || 'Timing update failed');
      await loadTasks(page, searchQuery, { silent: true });
    } finally {
      setUpdating(null);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / TICKET_LIST_PAGE_SIZE));
  const from = total === 0 ? 0 : (page - 1) * TICKET_LIST_PAGE_SIZE + 1;
  const to = Math.min(page * TICKET_LIST_PAGE_SIZE, total);
  const canPrev = page > 1;
  const canNext = page < totalPages;

  if (loading && tasks.length === 0) {
    return <p style={{ color: '#6B7280' }}>Loading your tasks…</p>;
  }

  return (
    <div>
      <h2 style={pageTitleStyle}>My Tasks{selectedProduct ? ` · ${selectedProduct.name}` : ''}</h2>
      <p style={pageSubtitleStyle}>
        Assigned in-progress tickets — Start work, Hold when you need to pause for other work, then Resume to continue.
        {' '}
        {!loading ? (total === 0 ? '0 tasks' : `${from}–${to} of ${total}`) : ''}
      </p>

      {error && <div style={alertError}>{typeof error === 'string' ? error : JSON.stringify(error)}</div>}

      <ListSearch
        value={searchQuery}
        onChange={setSearchQuery}
        placeholder="Search ticket no, subject, client…"
        resultCount={tasks.length}
        totalCount={total}
        style={{ marginBottom: '6px' }}
        inputStyle={{ padding: '6px 28px 6px 32px', fontSize: '12px', borderRadius: '6px' }}
      />

      {total === 0 ? (
        <p style={{ color: '#6B7280' }}>
          {searchQuery.trim() ? 'No tasks match that search.' : 'No tasks assigned to you.'}
        </p>
      ) : (
        <>
          <div style={{ overflowX: 'auto', opacity: loading ? 0.7 : 1 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead>
                <tr style={{ background: '#F9FAFB', textAlign: 'left' }}>
                  <th style={thStyle}>Ticket No</th>
                  <th style={thStyle}>Domain</th>
                  <th style={thStyle}>Category</th>
                  <th style={thStyle}>Client / Branch</th>
                  <th style={thStyle}>Subject</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Timing</th>
                  <th style={thStyle}>Due (IST)</th>
                  <th style={thStyle}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {tasks.map((t) => {
                  const timingState = normalizeTiming(t.timing_status);
                  const actions = actionsForTiming(t.timing_status);
                  const busy = updating === t.ticket_no;
                  return (
                    <tr key={t.ticket_no} style={{ borderBottom: '1px solid #E5E7EB' }}>
                      <td style={tdStyle}>
                        <TicketDetailLink ticketNo={t.ticket_no} style={{ color: '#002FA7', fontWeight: '600' }}>
                          {t.ticket_no}
                        </TicketDetailLink>
                      </td>
                      <td style={tdStyle}>{t.domain}</td>
                      <td style={tdStyle}>{ticketCategoryLabel(t)}</td>
                      <td style={{ ...tdStyle, maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={ticketClientBranchLabel(t)}>
                        {ticketClientBranchLabel(t)}
                      </td>
                      <td style={tdStyle}>{t.subject}</td>
                      <td style={tdStyle}><TicketStatusBadge status={t.status} /></td>
                      <td style={tdStyle}>
                        <span
                          style={{
                            display: 'inline-block',
                            padding: '2px 8px',
                            borderRadius: '999px',
                            fontSize: '12px',
                            fontWeight: '600',
                            background:
                              timingState === 'in_progress' ? '#D1FAE5'
                                : timingState === 'on_hold' ? '#FEF3C7'
                                  : '#F3F4F6',
                            color:
                              timingState === 'in_progress' ? '#065F46'
                                : timingState === 'on_hold' ? '#92400E'
                                  : '#374151',
                          }}
                        >
                          {TIMING_LABELS[timingState]}
                        </span>
                      </td>
                      <td style={tdStyle}>{t.due_date ? formatUtcToIst(t.due_date) : '—'}</td>
                      <td style={tdStyle}>
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                          {actions.map((action) => (
                            <button
                              key={action.key}
                              type="button"
                              disabled={busy}
                              onClick={() => handleTiming(t.ticket_no, action.key)}
                              style={{
                                ...btnSecondary,
                                background: action.color,
                                color: 'white',
                                border: 'none',
                                opacity: busy ? 0.6 : 1,
                                cursor: busy ? 'wait' : 'pointer',
                              }}
                            >
                              {busy ? 'Updating…' : action.label}
                            </button>
                          ))}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
            <span style={{ fontSize: 12, color: '#6B7280' }}>
              Page {page} of {totalPages}
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                className="tk-btn tk-btn-secondary tk-btn-sm"
                disabled={!canPrev || loading}
                onClick={() => setPage((p) => p - 1)}
              >
                <CaretLeft size={14} /> Prev
              </button>
              <button
                type="button"
                className="tk-btn tk-btn-secondary tk-btn-sm"
                disabled={!canNext || loading}
                onClick={() => setPage((p) => p + 1)}
              >
                Next <CaretRight size={14} />
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default TicketMyTasksPage;
